import { useImperativeHandle, useMemo } from 'react';
import { OnboardingHeader, CountryFlag } from '@tasama/ui';
import { CompanyDetailsForm } from '@/shared/components/CompanyDetailsForm';
import { useCompanyFormLogic } from '../../hooks/useCompanyFormLogic';
import { useCompanyDetails } from '../../hooks/useMyAccountApi';
import { mapCompanyDetailsToFormData } from './companyDataMapper';
import {
  INDUSTRY_SECTORS,
  COUNTRIES,
  GCC_COUNTRIES,
  COMPANY_TYPES,
} from '@/schemas/company-details.schema';
import type { CompanyType } from './types';
import { useCountries, useGCCCountries } from '@tasama/utils';
import { useTranslation } from '@tasama/i18n';

export interface CompanyDetailsEditHandle {
  validate: () => Promise<boolean>;
  getPayload: () => Record<string, unknown>;
}

interface CompanyDetailsEditPageProps {
  /** Ref object to expose { validate, getPayload } to the parent layout */
  handleRef?: React.RefObject<CompanyDetailsEditHandle | null>;
  testID?: string;
}

/**
 * Company Details Edit Page
 *
 * A dedicated page for editing company details with a different layout.
 * This page is accessed as a child route of my-account and uses CompanyEditLayout.
 * Provides a focused editing experience separate from the main dashboard.
 *
 * Exposes { validate, getPayload } via handleRef / useImperativeHandle so that
 * CompanyEditLayout can orchestrate multi-step submission without reading Zustand store.
 * Uses a plain prop instead of forwardRef to keep the component signature simple.
 */
export const CompanyDetailsEditPage: React.FC<CompanyDetailsEditPageProps> = ({
  handleRef,
  testID = 'company-details-edit-page',
}) => {
  const { t } = useTranslation('portal');

  // Get company data from React Query
  const { data: companyDetails } = useCompanyDetails();

  // TDF-1310 / TDF-1311: while a conversion is pending (International -> KSA post-TBL, or
  // PIF project -> PIF established) the client is establishing a NEW entity, so render the TARGET
  // company type's field set and start the form blank. Route-independent (Edit CTA or /kyb task).
  const conversion = companyDetails?.conversion;

  const companyType =
    ((conversion?.pending
      ? conversion.targetCompanyType
      : companyDetails?.companyType) as CompanyType) || 'PIF_ESTABLISHED';

  // Map company details to form data format (blank for a pending conversion - new entity from scratch)
  const initialFormData = useMemo(() => {
    if (!companyDetails || conversion?.pending) return {};
    return mapCompanyDetailsToFormData(companyDetails);
  }, [companyDetails, conversion?.pending]);

  // Use the custom hook for form logic with initial data
  const {
    formData,
    errors,
    companyTypeData,
    handleFieldChange,
    handleFieldBlur,
    isFieldRequired,
    shouldRenderField,
    getFieldLabel,
    trigger,
    getValues,
  } = useCompanyFormLogic({ companyType, initialData: initialFormData });

  // Fetch countries from API using centralized hooks from @tasama/utils
  const { data: countries = [], isLoading: countriesLoading } = useCountries();
  const { data: gccCountries = [] } = useGCCCountries();

  const countriesWithFlags = useMemo(
    () =>
      countries.map((c) => ({
        ...c,
        icon: <CountryFlag countryCode={c.value} />,
      })),
    [countries]
  );

  const gccCountriesWithFlags = useMemo(
    () =>
      gccCountries.map((c) => ({
        ...c,
        icon: <CountryFlag countryCode={c.value} />,
      })),
    [gccCountries]
  );

  // Expose validate + getPayload to CompanyEditLayout via handleRef
  useImperativeHandle(handleRef, () => ({
    validate: async () => trigger(),

    getPayload: () => {
      const values = getValues();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiPayload: any = { ...values };

      // Convert registration date DD/MM/YYYY → YYYY-MM-DD
      if (apiPayload.registrationStartDate) {
        const parts = (apiPayload.registrationStartDate as string).split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          apiPayload.registrationStartDate = `${year}-${month}-${day}`;
        }
      }

      // Convert industrySector value → label, rename to 'industry' for BFF
      if (apiPayload.industrySector) {
        const sector = INDUSTRY_SECTORS.find(
          (s) => s.value === apiPayload.industrySector
        );
        apiPayload.industry = sector ? sector.label : apiPayload.industrySector;
        delete apiPayload.industrySector;
      }

      // Resolve country: map value → label using appropriate list
      const countryOptions =
        companyTypeData.companyType === COMPANY_TYPES.INTERNATIONAL
          ? COUNTRIES
          : GCC_COUNTRIES;

      if (apiPayload.country) {
        const match = countryOptions.find(
          (o) => o.value === apiPayload.country
        );
        apiPayload.country = match ? match.label : apiPayload.country;
      }

      // countryOfHeadquarter → country (INTERNATIONAL companies)
      if (apiPayload.countryOfHeadquarter) {
        const match = COUNTRIES.find(
          (o) => o.value === apiPayload.countryOfHeadquarter
        );
        apiPayload.country = match
          ? match.label
          : apiPayload.countryOfHeadquarter;
      }
      delete apiPayload.countryOfHeadquarter;

      return apiPayload as Record<string, unknown>;
    },
  }));

  return (
    <div className="space-y-6">
      <OnboardingHeader
        size="lg"
        description=""
        title={t('companyForm.title')}
        icon="FileText"
        testID="details-header"
      />
      <CompanyDetailsForm
        formData={formData}
        errors={errors as Record<string, string>}
        companyTypeData={companyTypeData}
        onFieldChange={handleFieldChange}
        onFieldBlur={handleFieldBlur}
        isFieldRequired={isFieldRequired}
        shouldRenderField={shouldRenderField}
        getFieldLabel={getFieldLabel}
        countries={countriesWithFlags}
        gccCountries={gccCountriesWithFlags}
        countriesLoading={countriesLoading}
        variant="edit"
        testID={`${testID}-form`}
      />
    </div>
  );
};

CompanyDetailsEditPage.displayName = 'CompanyDetailsEditPage';

export default CompanyDetailsEditPage;
